import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Medal, Menu, RefreshCw, Search, X } from "lucide-react";
import { useAdminLayout } from "../AdminLayoutContext";

const VIEWS = [
  { id: "live", label: "Live" },
  { id: "history", label: "Hall of fame" },
];

export default function AdminLeaderboardChrome({
  view,
  onViewChange,
  search,
  onSearchChange,
  loading,
  onRefresh,
  totalRanked,
  page,
  totalPages,
}) {
  const { openMobileMenu } = useAdminLayout();
  const isLive = view === "live";

  const inputRef = useRef(null);
  const searchWrapRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const hasQuery = Boolean(String(search || "").trim());
  const isExpanded = searchOpen || hasQuery;

  const openSearch = useCallback(() => setSearchOpen(true), []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    onSearchChange("");
  }, [onSearchChange]);

  useEffect(() => {
    if (searchOpen && inputRef.current) inputRef.current.focus();
  }, [searchOpen]);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const handlePointerDown = (event) => {
      if (searchWrapRef.current?.contains(event.target)) return;
      if (!hasQuery) setSearchOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (hasQuery) onSearchChange("");
      else setSearchOpen(false);
      inputRef.current?.blur();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isExpanded, hasQuery, onSearchChange]);

  const handleBlur = () => {
    window.setTimeout(() => {
      if (
        !String(search || "").trim() &&
        !searchWrapRef.current?.contains(document.activeElement)
      ) {
        setSearchOpen(false);
      }
    }, 120);
  };

  return (
    <header className="alb-chrome">
      <div className="alb-chrome-row">
        <div className="alb-chrome-lead">
          <button
            type="button"
            className="alb-chrome-btn alb-chrome-btn-icon lg:hidden"
            onClick={openMobileMenu}
            aria-label="Open menu"
          >
            <Menu size={16} strokeWidth={2.2} />
          </button>

          <div className="alb-chrome-brand" aria-label="Leaderboard Arena">
            <Medal size={12} strokeWidth={2.3} aria-hidden />
            <span>Leaderboard Arena</span>
          </div>

          <div className="alb-chrome-tabs" role="tablist" aria-label="Leaderboard views">
            {VIEWS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={view === id}
                className={`alb-chrome-tab${view === id ? " alb-chrome-tab-active" : ""}`}
                onClick={() => onViewChange(id)}
              >
                {label}
              </button>
            ))}
          </div>

          {isLive ? (
            <div className="alb-chrome-stats">
              <span className="alb-chrome-stat tabular-nums">
                <em>{totalRanked ?? "—"}</em> ranked
              </span>
              {totalPages > 1 ? (
                <span className="alb-chrome-stat tabular-nums">
                  page <em>{page}</em> / {totalPages}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="alb-chrome-trail">
          {isLive ? (
            <div
              ref={searchWrapRef}
              className={`alb-chrome-search alb-chrome-search-expand ${
                isExpanded ? "alb-chrome-search-open" : ""
              } ${hasQuery ? "alb-chrome-search-active" : ""}`}
            >
              {!isExpanded ? (
                <button
                  type="button"
                  className="alb-chrome-search-toggle"
                  onClick={openSearch}
                  aria-label="Search leaderboard"
                  aria-expanded={false}
                >
                  <Search size={14} strokeWidth={2.3} aria-hidden />
                </button>
              ) : (
                <>
                  <Search size={14} aria-hidden className="alb-chrome-search-icon" />
                  <input
                    ref={inputRef}
                    type="search"
                    placeholder="Search name or code…"
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    onBlur={handleBlur}
                    aria-label="Search leaderboard"
                    aria-expanded
                  />
                  {hasQuery ? (
                    <button
                      type="button"
                      onClick={() => onSearchChange("")}
                      aria-label="Clear search"
                      className="alb-chrome-search-clear"
                    >
                      <X size={12} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={closeSearch}
                      aria-label="Close search"
                      className="alb-chrome-search-clear"
                    >
                      <X size={12} />
                    </button>
                  )}
                </>
              )}
            </div>
          ) : null}

          <button
            type="button"
            className="alb-chrome-btn alb-chrome-btn-icon"
            onClick={onRefresh}
            disabled={loading}
            aria-label="Refresh"
            title="Refresh"
          >
            {loading ? (
              <Loader2 size={14} className="am-spin" aria-hidden />
            ) : (
              <RefreshCw size={14} strokeWidth={2.3} aria-hidden />
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
